import React from 'react';
import FlexSearch from 'flexsearch';
import { sortNamedObjectsAlphabetically } from '../../helpers/stringWithId';
import { Column } from '../Column';
import { initializeContentTree } from './helpers/initializeContentTree';
import {
  FlexSearchIndex,
  HeaderWithItemsNodesAndIndices,
  NodeData,
  SearchAsYouTypeBoxProps,
  SearchAsYouTypeBoxState,
} from './typesSearchAsYouType';

/* Components */
export class SearchAsYouType extends React.PureComponent<SearchAsYouTypeBoxProps, SearchAsYouTypeBoxState> {
  static fillItemsSearchIndex = (itemsToBeAdded: Map<number, NodeData>): FlexSearchIndex => {
    const itemsSearchIndex = SearchAsYouType.createFlexSearchIndex();
    itemsToBeAdded.forEach(({ value }, idx) => itemsSearchIndex.add(idx, value));
    return itemsSearchIndex;
  };

  static createContentTreeRelatedStructures = (content: { id: string; selected: boolean; value: string }[]) => {
    const sortedNodes = sortNamedObjectsAlphabetically(content);
    const { contentTree, idToItemIdxMap, idxToHeaderMap, idxToItemMap } = initializeContentTree(sortedNodes);

    return {
      contentTree,
      idToItemIdxMap,
      idxToHeaderMap,
      idxToItemMap,
      itemsSearchIndex: SearchAsYouType.fillItemsSearchIndex(idxToItemMap),
    };
  };

  static createFlexSearchIndex = () => {
    const additionalMatcher = {
      /* eslint-disable prettier/prettier */
      'å': 'a',
      'æ': 'e',
      'ø': 'o',
      /* eslint-enable prettier/prettier */
    };

    const encoder = (str: string): string => {
      const trimmedStr = str.trim();
      return FlexSearch.encode('simple', trimmedStr);
    };

    return FlexSearch.create<number>({
      async: true,
      cache: true,
      encode: encoder,
      tokenize: 'forward',
    }).addMatcher(additionalMatcher);
  };

  static trimOfNonWordCharacters = (text?: string) => {
    if (!text) {
      return '';
    }

    // these regular expressions support all languages
    const trimNonWordCharactersIfAnyWordInside = /^([^a-z0-9\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*)([a-z0-9\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+(?:.+[a-z0-9\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*)([^a-z0-9\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*)$/i;
    const textPossiblyTrimmed = text.replace(trimNonWordCharactersIfAnyWordInside, '$2');

    const cleanIfNoWordCharacters = /^([^a-z0-9\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*)$/;
    const textTrimmed = textPossiblyTrimmed.replace(cleanIfNoWordCharacters, '');

    return textTrimmed;
  };

  constructor(props) {
    super(props);

    this.state = {
      searchBoxText: '',
      ...SearchAsYouType.createContentTreeRelatedStructures(props.content),
    };
  }

  /* eslint-disable react/no-did-update-set-state, react/destructuring-assignment */
  componentDidUpdate = (prevProps: Readonly<SearchAsYouTypeBoxProps>, prevState: Readonly<SearchAsYouTypeBoxState>) => {
    const { content } = this.props;
    const { itemsSearchIndex, searchBoxText } = this.state;

    if (prevProps.content !== content) {
      const contentTreeRelatedStructures = SearchAsYouType.createContentTreeRelatedStructures(content);
      this.setState(contentTreeRelatedStructures);
      return;
    }

    if (prevState.searchBoxText !== searchBoxText || prevState.itemsSearchIndex !== itemsSearchIndex) {
      const queryIndex = async (): Promise<void> => {
        const trimmedSearchBoxText = SearchAsYouType.trimOfNonWordCharacters(searchBoxText);
        const itemIndicesFound = await itemsSearchIndex.search(trimmedSearchBoxText);

        const trimmedSearchBoxTextWithNoWhitespaces = trimmedSearchBoxText.replace(/\s+/g, '');
        const noWhitespaceItemIndicesFound = await itemsSearchIndex.search(trimmedSearchBoxTextWithNoWhitespaces);

        const indicesSet = new Set<number>([...itemIndicesFound, ...noWhitespaceItemIndicesFound]);
        const allItemindicesFound = Array.from(indicesSet).sort();

        let indicesOfItemsToBeDisplay = [];
        if (trimmedSearchBoxText) {
          indicesOfItemsToBeDisplay = allItemindicesFound;
        }

        this.showOnlySelectedNodesIfSearchBoxHasWordCharacters(indicesOfItemsToBeDisplay);
      };
      queryIndex();
    }
  };
  /* eslint-enable react/no-did-update-set-state, react/destructuring-assignment */

  getNodesFromIndices = (): HeaderWithItemsNodesAndIndices[] => {
    const { contentTree, idxToHeaderMap, idxToItemMap } = this.state;
    const foundHeaderWithItemsNodes: HeaderWithItemsNodesAndIndices[] = [];

    contentTree.forEach(({ headerIndex, itemIndices }) => {
      const header = idxToHeaderMap.get(headerIndex);
      if (!header) {
        return;
      }
      const headerWithIndex = { ...header, idx: headerIndex };

      const itemNodes: (NodeData & { idx: number })[] = [];
      itemIndices.forEach(itemIndex => {
        const item = idxToItemMap.get(itemIndex);
        if (!item) {
          return;
        }
        const itemWithIndex = { ...item, idx: itemIndex };

        itemNodes.push(itemWithIndex);
      });

      foundHeaderWithItemsNodes.push({
        header: headerWithIndex,
        items: itemNodes,
      });
    });

    return foundHeaderWithItemsNodes;
  };

  showOnlySelectedNodesIfSearchBoxHasWordCharacters = (indicesOfItemsToBeDisplay: number[]): void => {
    const { idxToItemMap, searchBoxText } = this.state;
    const newIndexedItems = new Map();

    if (!SearchAsYouType.trimOfNonWordCharacters(searchBoxText)) {
      idxToItemMap.forEach((item, idx) => newIndexedItems.set(idx, { ...item, hidden: false }));
      this.setState({ idxToItemMap: newIndexedItems });
      return;
    }

    idxToItemMap.forEach((item, idx) => newIndexedItems.set(idx, { ...item, hidden: true }));

    if (indicesOfItemsToBeDisplay) {
      indicesOfItemsToBeDisplay.forEach(foundIndex => {
        const item = newIndexedItems.get(foundIndex);
        if (!item) {
          throw new Error(`item of index ${foundIndex} should exist in indexedItems`);
        }
        newIndexedItems.set(foundIndex, { ...item, hidden: false });
      });
    }

    this.setState({ idxToItemMap: newIndexedItems });
  };

  render = () => {
    const {
      headersWithItemsDisplay: { component: HeadersWithItemsDisplayComponent },
      OnAllAndNonzeroNodesHiddenComponent,
      searchBox: { component: SearchBoxComponent, underSearchBoxNode },
      onItemClicked,
      ...restProps
    } = this.props;

    const { idToItemIdxMap, idxToItemMap, searchBoxText } = this.state;

    return (
      <Column {...restProps}>
        <SearchBoxComponent
          handleInputChange={(text: string) => this.setState({ searchBoxText: text })}
          value={searchBoxText}
        />
        {underSearchBoxNode}
        <HeadersWithItemsDisplayComponent
          content={this.getNodesFromIndices()}
          forceHideHeaders={!!searchBoxText}
          onAllAndNonzeroNodesHidden={
            OnAllAndNonzeroNodesHiddenComponent ? (
              <OnAllAndNonzeroNodesHiddenComponent searchBoxValue={searchBoxText || ''} />
            ) : null
          }
          onItemClicked={
            onItemClicked
              ? ({ id }) => {
                  const itemIdx = idToItemIdxMap.get(id);
                  const { hidden, selected, value } = idxToItemMap.get(itemIdx);
                  onItemClicked({ hidden, id, selected, value });
                }
              : undefined
          }
        />
      </Column>
    );
  };
}
